import { useRef, useEffect, useState } from 'react';

import { FileInput } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { TLBaseShape, BaseBoxShapeUtil, HTMLContainer, RecordProps, T, stopEventPropagation, useDialogs } from 'tldraw';

import { NODE_HEIGHT, NODE_WIDTH } from '../../constants';
import { calculateNodeHeight, editNode } from '../../timelineDrawingHelpers';
import { NodeEditingModal } from '../Modals/NodeEditingModal';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useStore } from '@/store';

export type VisualChronologyNodeShape = TLBaseShape<
  'visual-chronology-node',
  {
    w: number;
    h: number;
    date: string;
    dateText: string;
    description: string;
    documentNames: string;
    docId: string;
    factId?: string;
    isAbove: boolean;
    nodeId: string;
  }
>;

export class VisualChronologyNode extends BaseBoxShapeUtil<VisualChronologyNodeShape> {
  static override type = 'visual-chronology-node' as const;
  static override props: RecordProps<VisualChronologyNodeShape> = {
    w: T.number,
    h: T.number,
    date: T.string,
    dateText: T.string,
    description: T.string,
    documentNames: T.string,
    docId: T.string,
    factId: T.string,
    isAbove: T.boolean,
    nodeId: T.string,
  };

  override canEdit() {
    return false;
  }

  override canResize() {
    return false;
  }

  getDefaultProps(): VisualChronologyNodeShape['props'] {
    return {
      w: NODE_WIDTH,
      h: NODE_HEIGHT,
      date: '',
      dateText: '',
      description: '',
      documentNames: '',
      docId: '',
      factId: '',
      isAbove: false,
      nodeId: '',
    };
  }

  component(shape: VisualChronologyNodeShape) {
    const { subsetId } = useParams();
    if (!subsetId) return null;

    const { addDialog } = useDialogs();
    const [calculatedHeight, setCalculatedHeight] = useState(shape.props.h);

    const { openReferenceDrawer } = useStore();

    const descriptionRef = useRef<HTMLDivElement>(null);

    // Calculate height when content changes
    useEffect(() => {
      const newHeight = calculateNodeHeight(shape.props.description, shape.props.documentNames);

      if (newHeight !== calculatedHeight) {
        setCalculatedHeight(newHeight);

        // Update the shape height if it's significantly different
        if (Math.abs(newHeight - shape.props.h) > 10) {
          this.editor.updateShape({
            id: shape.id,
            type: shape.type,
            props: {
              ...shape.props,
              h: newHeight,
            },
          });
        }
      }
    }, [
      shape.props.date,
      shape.props.description,
      shape.props.documentNames,
      calculatedHeight,
      shape.props.h,
      shape.id,
    ]);

    const openDoc = () => {
      openReferenceDrawer({ factId: shape.props.factId, caseId: subsetId });
      trackCustomEvent(TrackingEventsV2.DocViewerOpenFromVisualChronology);
    };

    const handleNodeClick = () => {
      // Open editing modal
      addDialog({
        component: ({ onClose }) => (
          <NodeEditingModal
            onClose={onClose}
            onSave={(nodeData) => handleModalSave(nodeData, onClose)}
            initialData={{
              dateText: shape.props.dateText,
              description: shape.props.description,
              documentNames: shape.props.documentNames,
            }}
          />
        ),
        onClose() {
          // Dialog closed without saving
        },
      });
    };

    const handleModalSave = (
      nodeData: { dateText: string; description: string; documentNames: string },
      onClose: () => void,
    ) => {
      // Use the new editNode function
      editNode(this.editor, shape.props.nodeId, {
        dateText: nodeData.dateText,
        description: nodeData.description,
        documentNames: nodeData.documentNames,
      });

      onClose();
    };

    return (
      <HTMLContainer
        id={shape.props.nodeId}
        onPointerDown={stopEventPropagation}
        style={{
          pointerEvents: 'all',
          borderRadius: 8,
          minWidth: `${NODE_WIDTH}px`,
          minHeight: `${NODE_HEIGHT}px`,
          height: `${calculatedHeight}px`,
        }}
        className="pointer-events-auto cursor-pointer rounded-lg border bg-white shadow-md"
        onClick={handleNodeClick}
      >
        <div className="flex h-full flex-col justify-between p-4">
          <div className="flex flex-col gap-2">
            <div className={`font-semibold text-gray-700`}>{shape.props.dateText}</div>
            <div ref={descriptionRef} className={`mb-2 flex whitespace-pre-wrap text-gray-600`}>
              {shape.props.description}
            </div>
          </div>
          {shape.props.documentNames && (
            <div
              className="group cursor-pointer rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-100 hover:shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                openDoc();
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="line-clamp-2 flex-1 text-xs text-blue-600">{shape.props.documentNames}</span>

                <FileInput
                  size={16}
                  className="flex-shrink-0 opacity-80 transition-all duration-200 group-hover:scale-110 group-hover:opacity-100"
                />
              </div>
            </div>
          )}
        </div>
      </HTMLContainer>
    );
  }

  indicator(shape: VisualChronologyNodeShape) {
    return <rect width={shape.props.w} height={shape.props.h} />;
  }

  // SVG export for tldraw
  override toSvg(shape: VisualChronologyNodeShape): React.ReactElement | null | Promise<React.ReactElement | null> {
    const { w, dateText, description, documentNames } = shape.props;
    const fontFamily = 'Inter, system-ui, sans-serif';

    // Use shape height for SVG
    const h = shape.props.h;

    // Helper to split description into lines for SVG (matching the actual text wrapping)
    const wrapText = (text: string, maxWidth: number, fontSize: number) => {
      const avgCharWidth = fontSize * 0.6; // Approximate character width
      const maxChars = Math.floor(maxWidth / avgCharWidth);

      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';

      words.forEach((word) => {
        if ((currentLine + word).length > maxChars) {
          if (currentLine) lines.push(currentLine.trim());
          currentLine = word + ' ';
        } else {
          currentLine += word + ' ';
        }
      });
      if (currentLine) lines.push(currentLine.trim());
      return lines;
    };

    // Calculate available width for text (accounting for padding)
    const textWidth = w - 32; // 16px padding on each side
    const dateFontSize = 12; // Smaller font size for date
    const descriptionFontSize = 10; // Smaller font size for description
    const docFontSize = 10; // Even smaller font size for document text

    const descLines = wrapText(description, textWidth, descriptionFontSize);
    const docLines = documentNames ? wrapText(documentNames.slice(0, 80), textWidth, docFontSize) : []; // Full width since no icon

    // Calculate positions based on actual spacing
    const padding = 16; // p-4 = 16px
    const gap = 12; // gap-3 = 12px
    const docPadding = 8; // p-2 = 8px
    const docBorderRadius = 4; // rounded = 4px

    let currentY = padding;

    // Date section
    const dateY = currentY + dateFontSize;
    currentY += dateFontSize + gap;

    // Description section
    const descStartY = currentY;
    const descLineHeight = 16; // Smaller line height for description
    currentY += descLines.length * descLineHeight + gap;

    // Document section (if exists) - positioned at bottom
    let docStartY = 0;
    if (documentNames) {
      const docSectionHeight = docLines.length * 12 + docPadding * 2; // Smaller line height for doc text
      docStartY = h - padding - docSectionHeight; // Position at bottom with padding
    }

    return (
      <svg width={w} height={h} style={{ pointerEvents: 'none' }}>
        {/* Background with shadow effect */}
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.1)" />
          </filter>
        </defs>

        {/* Main container */}
        <rect
          x={0}
          y={0}
          width={w}
          height={h}
          rx={8}
          fill="white"
          stroke="#e0e0e0"
          strokeWidth={1}
          filter="url(#shadow)"
        />

        {/* Date */}
        <text
          x={padding}
          y={dateY}
          fontSize={dateFontSize}
          fontWeight={600}
          fill="#374151"
          fontFamily={fontFamily}
          style={{ dominantBaseline: 'hanging' }}
        >
          {dateText}
        </text>

        {/* Description */}
        {descLines.map((line, i) => {
          const lineY = descStartY + i * descLineHeight + descriptionFontSize;
          return (
            <text
              key={i}
              x={padding}
              y={lineY}
              fontSize={descriptionFontSize}
              fill="#4B5563"
              fontFamily={fontFamily}
              style={{ whiteSpace: 'pre', dominantBaseline: 'hanging' }}
            >
              {line}
            </text>
          );
        })}

        {/* Document section */}
        {documentNames && (
          <g>
            {/* Document background */}
            <rect
              x={padding}
              y={docStartY}
              width={w - padding * 2}
              height={docLines.length * 12 + docPadding * 2}
              rx={docBorderRadius}
              fill="#EBF8FF"
              stroke="#BFDBFE"
              strokeWidth={1}
            />

            {/* Document text */}
            {docLines.map((line, i) => {
              const totalDocHeight = docLines.length * 12;
              const docBoxHeight = totalDocHeight + docPadding;
              const textStartY = docStartY + docPadding + (docBoxHeight - totalDocHeight) / 2 - 2; // Center text vertically in box
              const lineY = textStartY + i * 12; // Remove the +1 to start from the first line
              return (
                <text
                  key={i}
                  x={padding + docPadding}
                  y={lineY}
                  fontSize={docFontSize}
                  fill="#2563EB"
                  fontFamily={fontFamily}
                  textAnchor="start"
                  style={{ dominantBaseline: 'hanging' }}
                >
                  {line}
                </text>
              );
            })}
          </g>
        )}
      </svg>
    );
  }
}
